import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from "react-router-dom";

import { usePageTitle } from 'utils/pageTitle';
import { bulkDocumentRequest, clearDocumentRequestError, REQUEST_DOCUMENT_SUCCESS } from 'reducers/documentRequestReducer';
import { clearCart } from 'reducers/cartReducer';
import { isValidEmail } from 'utils/validators';

import SpinnerOverlay from 'components/SpinnerOverlay';

// import './CheckoutPage.css';
import './CartPage.css';


const CheckoutPage = (props) => {
	usePageTitle('Checkout');
	const dispatch = useDispatch();

	const {
		cartItems,
		userData,
	} = useSelector(state => ({
		cartItems: state.cart.items || [],
		userData: state.user.userData,
	}));

	useEffect(() => {
		if (userData?.email) {
			setEmail(userData.email);
			setIsEmailValid(isValidEmail(userData.email));
		}
		if (userData?.phone) {
			setPhone(userData.phone);
		}
	}, [userData]);

	const [email, setEmail] = useState('');
	const [name, setName] = useState('');
	const [phone, setPhone] = useState('');
	const [isEmailValid, setIsEmailValid] = useState('');
	const [isSuccess, setIsSuccess] = useState(false);
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	// const [sentToEmail, setSentToEmail] = useState('');

	const onChangeEmail = (emailString) => {
		setEmail(emailString);
		setIsEmailValid(isValidEmail(emailString));
	}

	const onConfirmCheckout = () => {
		if (isValidEmail && cartItems?.length) {
			setIsSuccess(false);
			setIsError(false);
			setErrorMessage('');
			setIsSubmitting(true);
			// setSentToEmail(email);

			// let promises = cartItems.map(item => dispatch(requestDocumentByEmail({
			// 	email,
			// 	phone,
			// 	name,
			// 	propertyId: item.propertyId,
			// 	documentName: item.documentName,
			// 	extra: item.extra || null,
			// })));


			// Promise.all(promises).then((values) => {
			// 	setIsSubmitting(false);
			// 	let someSucceeded = values.some(value => value.type === REQUEST_DOCUMENT_SUCCESS);
			// 	if (someSucceeded) {
			// 		setIsSuccess(true);
			// 		dispatch(clearCart());
			// 		dispatch(clearDocumentRequestError());
			// 	} else {
			// 		setIsError(true);
			// 	}
			// });

			dispatch(bulkDocumentRequest({
				email,
				phone,
				name,
				propertyId: cartItems[0]?.propertyId,
				items: cartItems,
			})).then((result) => {
				setIsSubmitting(false);
				if (result.type === REQUEST_DOCUMENT_SUCCESS) {
					setIsSuccess(true);
					dispatch(clearCart());
					dispatch(clearDocumentRequestError());
				} else {
					setIsError(true);
					// Extract error message from response if available
					const message = result.error?.response?.data?.message || 
					               result.error?.message || 
					               'Something went wrong. Your order was not able to be submitted.';
					setErrorMessage(message);
				}
			});
		}
	}

	if (isSuccess) {
		return (
			<div className="checkout-page">
				<div className="cart-page-inner">
					<div className="cart-card cart-confirmation">
						<div className="cart-confirmation-icon">
							<i className="bi bi-check-lg" />
						</div>
						<h1 className="cart-confirmation-title">Your order has been received!</h1>
						<p className="cart-confirmation-text">
							{`Violerts will follow up by email${email ? ` at ${email}` : ''} to deliver your documents and confirm any fees.`}
						</p>
						<div className="cart-confirmation-actions">
							<Link to="/dashboard/overview" className="cart-btn cart-btn-primary">
								Go to dashboard
							</Link>
							<Link to="/" className="cart-btn cart-btn-secondary">
								Find a property
							</Link>
						</div>
					</div>
				</div>
			</div>
		);
	}

	const canPlaceOrder = Boolean(
		email && isEmailValid && phone && name && cartItems.length,
	);
	// Only flag the email once something has been typed that isn't a valid address
	const showEmailError = Boolean(email) && !isEmailValid;

	return (
		<div className="checkout-page">
			<SpinnerOverlay
				isLoading={isSubmitting}
				text="Placing your order..."
				subtext="Sending your document request. This only takes a moment."
			/>
			<div className="cart-page-inner">
				<div className="cart-page-header">
					<h1 className="cart-page-title">Checkout</h1>
				</div>

				{isError && (
					<div className="cart-alert cart-alert-error" role="alert">
						{errorMessage || 'Something went wrong. Your order was not able to be submitted.'}
					</div>
				)}

				<div className="cart-layout">
					<div className="cart-card checkout-form">
						<h2 className="cart-section-title">Contact details</h2>
						<p className="cart-section-text">
							We&apos;ll use these to send your documents and any fee details.
						</p>
						<div className="checkout-field">
							<label htmlFor="checkout-email">
								Email address <span className="checkout-required">*</span>
							</label>
							<input
								id="checkout-email"
								type="email"
								placeholder="you@example.com"
								className={`checkout-input ${showEmailError ? 'is-invalid' : ''}`}
								required
								value={email}
								onChange={(ev) => onChangeEmail(ev.target.value)}
								autoFocus
							/>
							{showEmailError && (
								<div className="checkout-field-error">Enter a valid email address</div>
							)}
						</div>
						<div className="checkout-field">
							<label htmlFor="checkout-name">
								Name <span className="checkout-required">*</span>
							</label>
							<input
								id="checkout-name"
								type="text"
								className="checkout-input"
								required
								value={name}
								onChange={ev => setName(ev.target.value)}
								placeholder="Your name"
							/>
						</div>
						<div className="checkout-field">
							<label htmlFor="checkout-phone">
								Phone number <span className="checkout-required">*</span>
							</label>
							<input
								id="checkout-phone"
								type="text"
								className="checkout-input"
								required
								value={phone}
								onChange={ev => setPhone(ev.target.value)}
								placeholder="(555)-555-5555"
							/>
						</div>
					</div>

					<aside className="cart-card cart-summary">
						<h2 className="cart-summary-title">Order summary</h2>
						<p className="cart-summary-note cart-summary-note-top">
							A request will be sent for the following documents:
						</p>
						{!cartItems.length && (
							<p className="cart-summary-note">
								Your cart is empty. <Link to="/">Find a property</Link> to request documents.
							</p>
						)}
						<ul className="checkout-summary-list">
							{cartItems.map(item => (
								<li key={item.cartItemId} className="checkout-summary-item">
									<div className="cart-item-name">{item.documentName}</div>
									{item.extra && <div className="cart-item-extra">{item.extra}</div>}
									<div className="cart-item-extra">{item.address}</div>
								</li>
							))}
						</ul>
						<button
							className="cart-btn cart-btn-primary cart-btn-block"
							disabled={!canPlaceOrder}
							onClick={() => onConfirmCheckout()}
						>
							Place order
						</button>
						<Link to="/cart" className="cart-back-link">
							<i className="bi bi-arrow-left" /> Back to cart
						</Link>
					</aside>
				</div>
			</div>
		</div>
	);
}

export default CheckoutPage;
